import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { localizeDocument } from './dom-translations';

export type Language = 'tr' | 'en';

const STORAGE_KEY = 'vgantt.language';
const LanguageContext = createContext<{ language: Language; setLanguage: (language: Language) => void } | null>(null);

function initialLanguage(): Language {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'en' ? 'en' : 'tr';
  } catch {
    return 'tr';
  }
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguage] = useState<Language>(initialLanguage);

  useEffect(() => {
    document.documentElement.lang = language;
    try { localStorage.setItem(STORAGE_KEY, language); } catch { /* storage can be disabled */ }
  }, [language]);

  useEffect(() => localizeDocument(language), [language]);

  return <LanguageContext.Provider value={{ language, setLanguage }}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLanguage must be used inside LanguageProvider.');
  return context;
}

export function localeFor(language: Language): string {
  return language === 'en' ? 'en-US' : 'tr-TR';
}
